import { maskPersonNameForExport } from '../../../utils/exportDesensitize';
import type {
    TrackingDriver,
    TrackingFeedEvent,
    TrackingKpiSnapshot,
    TrackingSearchHit,
    TrackingSearchScope,
    TrackingSortKey,
    TrackingStop,
    TrackingVehicleType,
} from './pickupTrackingTypes';

type LngLat = { lng: number; lat: number };

export const SEARCH_RESULT_LIMIT = 8;
export const FEED_MAX_LIVE = 3;
/** feed 排队弹出的最小间隔 */
export const FEED_MIN_GAP_MS = 1_000;

export type VehicleFilter = 'all' | TrackingVehicleType;
/** -1 降序 / 1 升序 */
export type SortDir = -1 | 1;

/** 已处理（成功 + 失败）占预约总量的比例；总量为 0 时按 0 */
export const completionRate = (d: Pick<TrackingDriver, 'ok' | 'fail' | 'total'>): number =>
    d.total > 0 ? (d.ok + d.fail) / d.total : 0;

/** 成功占预约总量的比例（皇冠、进度条用）；总量为 0 时按 0 */
export const successRate = (d: Pick<TrackingDriver, 'ok' | 'total'>): number =>
    d.total > 0 ? d.ok / d.total : 0;

export const toPercent = (part: number, total: number): number =>
    total > 0 ? Math.round((part / total) * 100) : 0;

export const filterDrivers = (
    drivers: TrackingDriver[],
    vehicle: VehicleFilter,
    alertOnly: boolean,
): TrackingDriver[] =>
    drivers.filter(
        (d) => (vehicle === 'all' || d.vehicleType === vehicle) && (!alertOnly || d.anomalies.length > 0),
    );

/** 稳定排序，不改入参 */
export const sortDrivers = (
    drivers: TrackingDriver[],
    key: TrackingSortKey,
    dir: SortDir,
): TrackingDriver[] => {
    const val = (d: TrackingDriver) => (key === 'vol' ? d.total : completionRate(d));
    return drivers
        .map((d, i) => ({ d, i }))
        .sort((a, b) => {
            const x = val(a.d);
            const y = val(b.d);
            const cmp = x < y ? -1 : x > y ? 1 : 0;
            return cmp * dir || a.i - b.i;
        })
        .map((it) => it.d);
};

export const visibleDrivers = (
    drivers: TrackingDriver[],
    vehicle: VehicleFilter,
    alertOnly: boolean,
    sortKey: TrackingSortKey,
    sortDir: SortDir,
): TrackingDriver[] => sortDrivers(filterDrivers(drivers, vehicle, alertOnly), sortKey, sortDir);

/** 成功率最高者戴皇冠；并列取列表中靠前的；没有有效司机返回 null */
export const kingKey = (drivers: TrackingDriver[]): string | null => {
    let best: TrackingDriver | null = null;
    let bestVal = -1;
    drivers.forEach((d) => {
        if (d.total <= 0) return;
        const r = successRate(d);
        if (r > bestVal) {
            bestVal = r;
            best = d;
        }
    });
    return best ? (best as TrackingDriver).key : null;
};

/** 异常司机数；列表「仅异常」按当前车型计（与打开「仅异常」后的列表人数一致），KPI 司机卡按全部 */
export const alertCount = (drivers: TrackingDriver[], vehicle: VehicleFilter = 'all'): number =>
    filterDrivers(drivers, vehicle, true).length;

export const vehicleCounts = (
    drivers: TrackingDriver[],
): Record<VehicleFilter, number> => ({
    all: drivers.length,
    van: drivers.filter((d) => d.vehicleType === 'van').length,
    t26: drivers.filter((d) => d.vehicleType === 't26').length,
    t53: drivers.filter((d) => d.vehicleType === 't53').length,
});

/**
 * 大屏展示姓名：法务要求脱敏，照派送大屏用 maskPersonNameForExport；
 * 没有姓名时 name 已回落为工号，工号原样展示。搜索仍按原名匹配
 */
export const displayName = (d: Pick<TrackingDriver, 'name' | 'id'>): string =>
    d.name === d.id ? d.id : maskPersonNameForExport(d.name) || d.id;

// 姓名首字母缩写，照抄派送大屏 DeliveryMonitorPage.initials。姓名在大屏上脱敏展示（法务要求），缩写只能取脱敏后仍可见的各词首字：
// 中文取首字，英文多词取首尾词首字母，单词取首字母。空则取 fallback（工号）前 2 位
export const initials = (name: string | null | undefined, fallback: string): string => {
    const s = (name || '').trim();
    if (!s) return fallback.slice(0, 2).toUpperCase();
    if (/[一-龥]/.test(s)) return s.slice(0, 1);
    const parts = s.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return s.slice(0, 1).toUpperCase();
};

/** 站点停留是否超过阈值；待上门站点不算 */
export const isLongStay = (stop: TrackingStop, thresholdMin: number): boolean =>
    stop.status !== 'wait' && stop.stayMin !== null && stop.stayMin > thresholdMin;

/** 下一站：第一个 cur / wait；全部处理完返回 -1 */
export const nextStopIndex = (d: TrackingDriver): number =>
    d.stops.findIndex((s) => s.status === 'wait' || s.status === 'cur');

export interface ReplaySummary {
    ok: number;
    fail: number;
    /** 待上门（含 cur） */
    wait: number;
    long: number;
}

export const replaySummary = (d: TrackingDriver, thresholdMin: number): ReplaySummary => {
    const sum: ReplaySummary = { ok: 0, fail: 0, wait: 0, long: 0 };
    d.stops.forEach((s) => {
        if (s.status === 'ok') sum.ok += 1;
        else if (s.status === 'fail') sum.fail += 1;
        else sum.wait += 1;
        if (isLongStay(s, thresholdMin)) sum.long += 1;
    });
    return sum;
};

/** 站点坐标；未解析（lat / lng 任一为 null）返回 null，不上图 */
export const stopPoint = (s: TrackingStop): LngLat | null =>
    s.lng === null || s.lat === null ? null : { lng: s.lng, lat: s.lat };

const locatedPoints = (stops: TrackingStop[]): LngLat[] =>
    stops.map(stopPoint).filter((p): p is LngLat => p !== null);

/**
 * 轨迹回放途经点：[首个已访问站, ...已访问站, 当前位置]；没有已访问站时退化为第一站；无位置则不含末尾；无坐标的站跳过。
 * 回放已改用 ClickHouse GPS 轨迹（同派送大屏），此函数暂无调用方；按用户要求保留，以后可能还会用。
 */
export const replayPoints = (d: TrackingDriver): LngLat[] => {
    let visited = locatedPoints(d.stops.filter((s) => s.status !== 'wait'));
    if (visited.length === 0) visited = locatedPoints(d.stops).slice(0, 1);
    if (visited.length === 0) return d.position ? [d.position] : [];
    const pts = [visited[0], ...visited].map((p) => ({ ...p }));
    if (d.position) pts.push({ lng: d.position.lng, lat: d.position.lat });
    return pts;
};

export const searchDrivers = (
    drivers: TrackingDriver[],
    scope: TrackingSearchScope,
    rawQuery: string,
): TrackingSearchHit[] => {
    const q = rawQuery.trim().toLowerCase();
    if (!q) return [];
    const hits: TrackingSearchHit[] = [];
    drivers.forEach((d) => {
        if (scope === 'driver') {
            if (d.name.toLowerCase().includes(q) || d.id.toLowerCase().includes(q)) {
                hits.push({ driver: d, stopIdx: null });
            }
            return;
        }
        d.stops.forEach((s, i) => {
            const hay = scope === 'site' ? `${s.siteName} ${s.siteId}` : s.address;
            if (hay.toLowerCase().includes(q)) hits.push({ driver: d, stopIdx: i });
        });
    });
    return hits;
};

/** 键盘 ↑↓ 在 n 条结果间循环；n 为 0 时保持原值 */
export const cycleIndex = (cur: number, n: number, step: 1 | -1): number =>
    n > 0 ? (cur + step + n) % n : cur;

/** 当前选中项下标不超出可见结果 */
export const clampSearchIndex = (idx: number, found: number): number =>
    Math.min(idx, Math.max(0, Math.min(found, SEARCH_RESULT_LIMIT) - 1));

export type DeltaKind = 'pct' | 'abs';
export type DeltaTone = 'good' | 'bad' | 'flat';

export interface DeltaView {
    /** 带正负号的数值文本，如 `+6.2%` / `+2` */
    text: string;
    up: boolean;
    tone: DeltaTone;
}

/**
 * 较昨日同 slot：对比期缺失返回 null（界面显示 `-`），不编造。
 * pct 方式昨日为 0 时没有意义，同样返回 null；abs 方式直接取差值。
 * lowerIsBetter：失败量下降为好。
 */
export const computeDelta = (
    current: number,
    yesterday: number | null,
    kind: DeltaKind,
    lowerIsBetter: boolean,
): DeltaView | null => {
    if (yesterday === null) return null;
    let diff: number;
    let text: string;
    if (kind === 'pct') {
        if (yesterday === 0) return null;
        diff = ((current - yesterday) / yesterday) * 100;
        const rounded = Math.round(Math.abs(diff) * 10) / 10;
        text = `${diff >= 0 ? '+' : '-'}${rounded.toFixed(1)}%`;
        if (rounded === 0) diff = 0;
    } else {
        diff = current - yesterday;
        text = `${diff >= 0 ? '+' : '-'}${Math.abs(diff)}`;
    }
    if (diff === 0) return { text, up: true, tone: 'flat' };
    const up = diff > 0;
    return { text, up, tone: up !== lowerIsBetter ? 'good' : 'bad' };
};

export interface KpiRateText {
    okRate: string;
    failRate: string;
}

type KpiCounts = Pick<TrackingKpiSnapshot, 'appointments' | 'pickedUp' | 'drivers' | 'failed'>;

/** 补间值是浮点，同派送 formatCount 先 Math.round；展示和 kpiRates 都用取整后的值，率只在显示整数跳格时才动 */
export const roundKpi = (kpi: KpiCounts): KpiCounts => ({
    appointments: Math.round(kpi.appointments),
    pickedUp: Math.round(kpi.pickedUp),
    drivers: Math.round(kpi.drivers),
    failed: Math.round(kpi.failed),
});

/** 成功率 / 失败率，分母为 0 时 0.0；由取整后的分子分母推导，与补间展示数字一致 */
export const kpiRates = (kpi: Pick<TrackingKpiSnapshot, 'appointments' | 'pickedUp' | 'failed'>): KpiRateText => {
    const rate = (n: number) => (kpi.appointments > 0 ? ((n / kpi.appointments) * 100).toFixed(1) : '0.0');
    return { okRate: rate(kpi.pickedUp), failRate: rate(kpi.failed) };
};

/** 追加一条 feed，超出 max 条「在场」的挤掉最旧的；返回新列表与被挤掉的 id（调用方负责延时移除） */
export const pushFeed = <T extends { id: number; out?: boolean }>(
    feed: T[],
    entry: T,
    max: number = FEED_MAX_LIVE,
): { feed: T[]; evicted: number[] } => {
    const next = [...feed, entry];
    const evicted: number[] = [];
    const live = next.filter((f) => !f.out);
    while (live.length > max) {
        const old = live.shift();
        if (old) evicted.push(old.id);
    }
    return {
        feed: next.map((f) => (evicted.includes(f.id) ? { ...f, out: true } : f)),
        evicted,
    };
};

export type FeedItem = TrackingFeedEvent & { out?: boolean };

/** 排队中的 count 条在 spanMs 内均匀弹出的间隔，下限 FEED_MIN_GAP_MS */
export const feedGapMs = (count: number, spanMs: number): number =>
    Math.max(FEED_MIN_GAP_MS, Math.floor(spanMs / Math.max(1, count)));

export type PickupEventDraft = Omit<TrackingFeedEvent, 'id'>;

const toEventDraft = (d: TrackingDriver, s: TrackingStop): PickupEventDraft => ({
    driverKey: d.key,
    driverName: displayName(d),
    siteName: s.siteName,
    fail: s.status === 'fail',
});

/** 按操作时间正序；没有操作时间的排最后，其余保持列表顺序（sort 稳定） */
const byOperatedAt = (a: { at: number | null }, b: { at: number | null }): number =>
    (a.at ?? Number.MAX_SAFE_INTEGER) - (b.at ?? Number.MAX_SAFE_INTEGER);

/**
 * 站点对齐 key：siteId + 该站在司机列表里第几次出现（同一站点一天可能有多个预约、排多次），siteId 缺失时退回下标。
 * 只用 siteId 时同站多条会互相覆盖，后一条的状态顶掉前一条，导致重复弹或漏弹。
 * 回放 marker 也按它绑定：站点按上门顺序排，状态一变下标就会挪，按下标绑会把别的站画到这个坐标上
 */
export const stopKeys = (stops: TrackingStop[]): string[] => {
    const seen = new Map<string, number>();
    return stops.map((s, i) => {
        if (!s.siteId) return `idx:${i}`;
        const n = seen.get(s.siteId) ?? 0;
        seen.set(s.siteId, n + 1);
        return `id:${s.siteId}#${n}`;
    });
};

/**
 * 前后两份快照对比出「刚完成」的站点（待上门 / 当前站 → 成功 / 失败，成功失败都弹），驱动实时 feed。
 * 站点按 siteId + 第几次出现对齐（缺失时退回下标）；上一份里没有的司机 / 站点不算，避免首屏或换范围时刷屏。
 * 不限条数、按操作时间正序，由 feed 队列在下一次刷新前摊匀逐条弹出。
 */
export const diffPickupEvents = (prev: TrackingDriver[], next: TrackingDriver[]): PickupEventDraft[] => {
    const before = new Map<string, Map<string, TrackingStop>>();
    prev.forEach((d) => {
        const keys = stopKeys(d.stops);
        before.set(d.key, new Map(d.stops.map((s, i) => [keys[i], s])));
    });
    const out: { at: number | null; e: PickupEventDraft }[] = [];
    next.forEach((d) => {
        const stops = before.get(d.key);
        if (!stops) return;
        const keys = stopKeys(d.stops);
        d.stops.forEach((s, i) => {
            const old = stops.get(keys[i]);
            if (!old || (old.status !== 'wait' && old.status !== 'cur')) return;
            if (s.status !== 'ok' && s.status !== 'fail') return;
            out.push({ at: s.operatedAtMs, e: toEventDraft(d, s) });
        });
    });
    return out.sort(byOperatedAt).map((x) => x.e);
};

/** 冷启动（新开页面 / 换范围后第一份快照）没有上一份可比：取操作时间最近的 max 条成功 / 失败站点兜底，正序 */
export const latestPickupEvents = (drivers: TrackingDriver[], max: number = FEED_MAX_LIVE): PickupEventDraft[] => {
    const out: { at: number; e: PickupEventDraft }[] = [];
    drivers.forEach((d) =>
        d.stops.forEach((s) => {
            if ((s.status === 'ok' || s.status === 'fail') && s.operatedAtMs !== null) {
                out.push({ at: s.operatedAtMs, e: toEventDraft(d, s) });
            }
        }),
    );
    return out
        .sort(byOperatedAt)
        .slice(-max)
        .map((x) => x.e);
};

/** 弹窗高度：有异常多一条告警条 */
export const popupHeight = (d: TrackingDriver): number => (d.anomalies.length ? 600 : 560);
